/* MadMouse — FX:
   1) Parallax on `.mm-parallax` (background image scrolls slower than viewport).
   2) Word-by-word reveal on `.mm-reveal-words` (heading splits into spans on enter).
   Honors `prefers-reduced-motion`. */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ===========================================================
     1. PARALLAX
     Each `.mm-parallax` element has an `<img>` child that gets
     `translate3d(0, Yvh, 0)` based on its scroll progress.
     The img is rendered 130% tall by CSS so 15% of movement is safe.
     =========================================================== */
  (function parallax() {
    if (reduceMotion) return;
    var targets = document.querySelectorAll('.mm-parallax');
    if (!targets.length) return;

    var ticking = false;
    var visible = new Set();

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) visible.add(e.target);
        else visible.delete(e.target);
      });
    }, { rootMargin: '20% 0px 20% 0px' });
    targets.forEach(function (t) { io.observe(t); });

    function update() {
      var vh = window.innerHeight;
      visible.forEach(function (root) {
        var img = root.querySelector('img');
        if (!img) return;
        var rect = root.getBoundingClientRect();
        /* progress: -1 (just below viewport) → 0 (center) → +1 (just above) */
        var center = rect.top + rect.height / 2;
        var progress = (center - vh / 2) / (vh / 2 + rect.height / 2);
        progress = Math.max(-1, Math.min(1, progress));
        /* Move the image up to ±10% of its container height */
        var shift = -progress * 12;
        img.style.transform = 'translate3d(0,' + shift + '%, 0)';
      });
      ticking = false;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  })();

  /* ===========================================================
     2. CAROUSEL MAGNIFICATION (macOS Dock — continuous, mouse-tracked)
     For each `.mm-carousel__track`:
     - On mousemove, every slide's scale = f(distance from cursor X)
     - Cosine falloff within INFLUENCE px → smooth wave
     - On mouseleave, reset all to scale 1
     - Only desktop (hover: hover) — touch falls back to static grid
     =========================================================== */
  (function carouselMagnify() {
    if (reduceMotion) return;
    if (!window.matchMedia('(hover: hover)').matches) return;

    var tracks = document.querySelectorAll('.mm-carousel__track');
    if (!tracks.length) return;

    var MAX_SCALE = 1.324; /* −10% del 1.36 anterior (zoom de la activa) */
    var INFLUENCE = 900; /* px radius — alcanza al slide a +2 de distancia */

    tracks.forEach(function (track) {
      var slides = Array.prototype.slice.call(track.querySelectorAll('.mm-carousel__slide'));
      if (!slides.length) return;

      var ticking = false;
      var cursorX = 0;

      function apply() {
        ticking = false;
        for (var i = 0; i < slides.length; i++) {
          var rect = slides[i].getBoundingClientRect();
          var center = rect.left + rect.width / 2;
          var d = Math.abs(cursorX - center);
          var t = Math.max(0, 1 - d / INFLUENCE);
          /* cubic ease — pico afilado, vecinos a ~mitad de su zoom anterior */
          var eased = t * t * t;
          var scale = 1 + (MAX_SCALE - 1) * eased;
          slides[i].style.transform = 'scale(' + scale.toFixed(3) + ')';
          slides[i].style.zIndex = Math.round(scale * 10);
        }
      }

      track.addEventListener('mousemove', function (e) {
        cursorX = e.clientX;
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(apply);
      }, { passive: true });

      track.addEventListener('mouseleave', function () {
        for (var i = 0; i < slides.length; i++) {
          slides[i].style.transform = '';
          slides[i].style.zIndex = '';
        }
      });
    });
  })();

  /* ===========================================================
     3. WORD-BY-WORD REVEAL
     For each `.mm-reveal-words`:
     - Splits visible text nodes into <span>s per word.
     - On enter viewport → cascade in with staggered delay.
     - Inline <strong>/<b>/<em> are preserved.
     =========================================================== */
  (function wordReveal() {
    var targets = document.querySelectorAll('.mm-reveal-words');
    if (!targets.length) return;

    function splitNode(node) {
      if (node.nodeType === Node.TEXT_NODE) {
        var text = node.textContent;
        if (!text.trim()) return null;
        var frag = document.createDocumentFragment();
        var words = text.split(/(\s+)/);
        words.forEach(function (w) {
          if (!w) return;
          if (/^\s+$/.test(w)) {
            frag.appendChild(document.createTextNode(w));
          } else {
            var span = document.createElement('span');
            span.className = 'mm-reveal-word';
            span.textContent = w;
            frag.appendChild(span);
          }
        });
        return frag;
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        /* Recurse into children, preserving the element */
        var clone = node.cloneNode(false);
        Array.from(node.childNodes).forEach(function (child) {
          var processed = splitNode(child);
          if (processed) clone.appendChild(processed);
          else clone.appendChild(child.cloneNode(true));
        });
        return clone;
      }
      return null;
    }

    targets.forEach(function (root) {
      /* Split */
      var newRoot = splitNode(root);
      if (newRoot && newRoot !== root) {
        while (root.firstChild) root.removeChild(root.firstChild);
        while (newRoot.firstChild) root.appendChild(newRoot.firstChild);
      }

      /* Stagger CSS variable */
      var words = root.querySelectorAll('.mm-reveal-word');
      words.forEach(function (w, i) {
        w.style.setProperty('--mm-word-delay', (i * 60) + 'ms');
      });

      if (reduceMotion) {
        root.classList.add('is-visible');
        return;
      }

      /* Enter viewport → animate */
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            root.classList.add('is-visible');
            io.unobserve(root);
          }
        });
      }, { threshold: 0.25 });
      io.observe(root);
    });
  })();
})();
